import { Platform } from 'react-native';
import api, { FetchError } from '../api/api';
import { addDays } from 'date-fns';
import * as WebBrowser from 'expo-web-browser';
import * as Linking from 'expo-linking';
import { UserAttributeType } from '../models/UserAttribute';

export type GarminToken = {
  key: string;
  secret: string;
};

export async function fetchGarminData(
  type: UserAttributeType
): Promise<any | undefined> {
  const startDate = addDays(new Date(), -30);
  startDate.setUTCHours(0, 0, 0, 0);
  try {
    const result = await api.post('garmin/data', {
      type: type.valueOf(),
      startDate: startDate,
      endDate: new Date()
    });
    const response = await result.response;
    const data = await response.json();
    return data;
  } catch (error) {
    return undefined;
  }
}

export function convertSecondsToDate(seconds: number): Date {
  const date = new Date(0);
  date.setUTCSeconds(seconds);
  return date;
}

export function convertSecondsToMinutes(seconds: number): number {
  return seconds / 60;
}

async function requestAccess(
  token: GarminToken,
  verifier: string
): Promise<GarminToken | undefined> {
  try {
    const result = await api.post('garmin/access-token', {
      oauthToken: token,
      verifier
    });
    const response = await result.response;
    if (!response.ok) {
      const error = (await response.json()) as FetchError;
      throw error;
    }
    const accessToken = await response.json();
    return accessToken;
  } catch (error: any) {
    console.debug('requestAccess error', error);
    throw error;
  }
}

export async function requestToken(): Promise<GarminToken | undefined> {
  try {
    const result = await api.get('garmin/oauth/token', {});
    const response = await result.response;
    if (!response.ok) {
      const error = (await response.json()) as FetchError;
      throw error;
    }
    const requestTokenParams = await response.text();

    let url: string | null = null;
    if (Platform.OS === 'web') url = await Linking.getInitialURL();
    let path: string | null = null;
    if (url) path = new URL(url).pathname;

    const handleResult = async (event: any) => {
      const url = new URL(event.url);
      const verifier = url.searchParams.get('oauth_verifier');
      const params = new URLSearchParams(requestTokenParams);
      const token: GarminToken = {
        key: params.get('oauth_token') || '',
        secret: params.get('oauth_token_secret') || ''
      };
      if (verifier && verifier != 'null') {
        return await requestAccess(token, verifier);
      } else {
        return undefined;
      }
    };

    let androidAuthResult = undefined;
    const urlListener = Linking.addEventListener('url', event => {
      if (Platform.OS === 'android' && event.url) {
        androidAuthResult = event;
      }
    });

    const authSessionResult = await WebBrowser.openAuthSessionAsync(
      'https://connect.garmin.com/oauthConfirm?' +
        requestTokenParams +
        `&oauth_callback=${Linking.createURL(path || '/')}`
    );
    urlListener.remove();
    if (authSessionResult.type === 'success') {
      return await handleResult(authSessionResult);
    } else if (Platform.OS === 'android' && androidAuthResult) {
      return await handleResult(androidAuthResult);
    }
  } catch (error: any) {
    alert(error?.message || 'Something went wrong, try again.');
    throw error;
  }
  return undefined;
}

export async function revokeAccess(token: GarminToken) {
  try {
    const result = await api.post('garmin/revoke', { oauthToken: token });
    const response = await result.response;
    if (!response.ok) {
      const error = (await response.json()) as FetchError;
      throw error;
    }
  } catch (error: any) {
    alert(error?.message || 'Something went wrong, try again.');
    throw error;
  }
}
